// What the "Basis" column says, and why it never just says "no evidence".
//
// Missing has to mean one thing: every place a person can be accounted for
// was checked and none of them had anything. When something ALMOST counted —
// an approved client visit nobody checked into, a half day's leave, a
// clock-in with no clock-out — a row reading "no evidence" sends a manager
// after someone who did nothing wrong. Do that twice and the column stops
// being read, which is the opposite of what makes people keep the tool fed.

export interface BasisText {
  /** One short line for the table cell. */
  label: string;
  /** The longer sentence, for a tooltip — what to actually do about it. */
  detail?: string;
  /** True when a record exists somewhere and is the reason this is not clean. */
  actionable?: boolean;
}

const RULES: Record<string, BasisText> = {
  attendance_scan: { label: 'Attendance scan' },
  approved_leave: { label: 'Approved leave' },
  wfh_leave: { label: 'Approved work from home' },
  assignment: { label: 'Work assignment' },
  day_kind: { label: 'Rest day or public holiday' },
  expected_remote: {
    label: 'Remote profile — unconfirmed',
    detail: 'Their profile says remote, but nothing confirms they worked. A profile setting is not a record.',
  },
  no_evidence: {
    label: 'Nothing anywhere',
    detail: 'No scan, no approved leave, no work assignment, not a rest day, no remote profile. This one is genuinely unaccounted for.',
  },
  assignment_not_checked_in: {
    label: 'Assignment — never checked in',
    detail: 'There is an approved assignment for this day and nobody checked in against it, so it cannot count as presence. Chase the check-in, not the person.',
    actionable: true,
  },
  assignment_awaiting_approval: {
    label: 'Assignment — awaiting approval',
    detail: 'They raised an assignment for this day and nobody has approved it. The gap is an approver.',
    actionable: true,
  },
  assignment_rejected: {
    label: 'Assignment — rejected',
    detail: 'An assignment was raised for this day and rejected, so the day is still unaccounted for.',
    actionable: true,
  },
  leave_awaiting_approval: {
    label: 'Leave requested — awaiting approval',
    detail: 'They asked for this day off and nobody has answered. The employee did their part; the gap is an approver.',
    actionable: true,
  },
  half_day_leave_no_scan: {
    label: 'Half day leave — no scan',
    detail: 'Half the day is approved leave. The half they were due to work has no scan.',
    actionable: true,
  },
  scan_without_hours: {
    label: 'Scan with no hours',
    detail: 'A scan exists but carries no worked time — usually a missing clock-out. Fix the day rather than chasing the person.',
    actionable: true,
  },
};

export const basisOf = (evidence: Record<string, any> | null | undefined): BasisText => {
  const rule = String(evidence?.rule || '');
  if (RULES[rule]) return RULES[rule];
  // An unknown rule from a newer server: show it rather than hiding it, but
  // de-slugged so it still reads like English.
  if (rule) return { label: rule.replace(/_/g, ' ') };
  return { label: '—' };
};

/** The sources consulted, for a "we did look" tooltip on a clean miss. */
export const checkedList = (evidence: Record<string, any> | null | undefined): string[] => {
  const c = evidence?.checked;
  return Array.isArray(c) ? c.map(String) : [];
};

/** What the worked-time cell says — one translatable line plus a tone. */
export interface WorkedText {
  key: string;
  defaultValue: string;
  values?: Record<string, string | number>;
  tone: 'default' | 'muted' | 'warning';
  /** A tooltip qualifying the figure (e.g. lateness/OT not applied). */
  note?: { key: string; defaultValue: string };
}

// The worked-time cell, and why it is not just `minutes`.
//
// ⚠️ A zero is never an answer by itself. On Fern's board (22 Oct 2026) one
// "0" meant "arrived at 08:18 and is still here", another "badged in, no
// departure recorded", another "before attendance was being measured" — and a
// running first-to-last total for a day still in progress read like hours
// worked. Each of those is said in words; only a finished day shows a number.
export const workedCell = (
  minutes: number | null | undefined,
  evidence: Record<string, any> | null | undefined,
): WorkedText => {
  const clockIn = typeof evidence?.clockIn === 'string' ? evidence.clockIn : '';
  if (evidence?.inProgress) {
    return clockIn
      ? { key: 'wa.worked_in_since', defaultValue: 'In since {{time}} · in progress', values: { time: clockIn }, tone: 'muted' }
      : { key: 'wa.worked_in_progress', defaultValue: 'In progress', tone: 'muted' };
  }
  if (evidence?.hoursWithheld) {
    // A row written before D4 zeroed its hours; they return once the day is
    // recalculated. Present, never "missing".
    return { key: 'wa.worked_withheld', defaultValue: 'Present · hours pending recalculation (before go-live)', tone: 'muted' };
  }
  if (evidence?.missingClockOut) {
    return clockIn
      ? { key: 'wa.worked_no_out_since', defaultValue: 'In {{time}} · no clock-out', values: { time: clockIn }, tone: 'warning' }
      : { key: 'wa.worked_no_out', defaultValue: 'No clock-out recorded', tone: 'warning' };
  }
  const m = Math.max(0, Math.round(Number(minutes) || 0));
  if (m > 0) {
    return {
      key: 'wa.worked_hm', defaultValue: '{{h}}h {{m}}m', values: { h: Math.floor(m / 60), m: m % 60 }, tone: 'default',
      // Before go-live the hours are facts; lateness and overtime were not applied.
      ...(evidence?.lateOtWithheld
        ? { note: { key: 'wa.worked_late_ot_withheld', defaultValue: 'Before go-live: lateness and overtime were not applied to this day.' } }
        : {}),
    };
  }
  return { key: 'wa.worked_none', defaultValue: '—', tone: 'muted' };
};

// ---------------------------------------------------------------- P2 helpers

export const fmtHM = (m: unknown): string => {
  const n = Math.max(0, Math.round(Number(m) || 0));
  return `${Math.floor(n / 60)}h ${n % 60}m`;
};

/** Where the row's evidence came from. Field work, leave and verified
 *  attendance are each their own chip — never conflated. */
export const SOURCE_CHIP: Record<string, { label: string; tone: string }> = {
  scanner: { label: 'Scanner', tone: 'bg-info-soft text-info-strong' },
  self: { label: 'Self check-in', tone: 'bg-info-soft text-info-strong' },
  self_backup: { label: 'Self (backup)', tone: 'bg-warning-soft text-warning-strong' },
  merged: { label: 'Scanner + self', tone: 'bg-info-soft text-info-strong' },
  manual: { label: 'Manual', tone: 'bg-warning-soft text-warning-strong' },
  field: { label: 'Field', tone: 'bg-success-soft text-success-strong' },
  leave: { label: 'Leave', tone: 'bg-muted text-foreground' },
};

export const DAY_STATUS: Record<string, { label: string; tone: string; hint: string }> = {
  in_progress: { label: 'In progress', tone: 'text-muted-foreground', hint: 'The day is not over — no total yet.' },
  complete: { label: 'Complete', tone: 'text-foreground', hint: 'A finished day with nothing open.' },
  needs_review: { label: 'Needs review', tone: 'text-warning', hint: 'Something is open: a missing clock-out, an exception, or nothing recorded.' },
  approved: { label: 'Approved', tone: 'text-success', hint: 'Approved by a reviewer (or approved leave).' },
  awaiting_sync: { label: 'Awaiting sync', tone: 'text-muted-foreground', hint: 'The scanner has not reported this yet — not evidence of absence.' },
};

export const sourceChipOf = (ev: Record<string, any> | null | undefined) =>
  SOURCE_CHIP[String(ev?.sourceChip || '')] || null;

export const dayStatusOf = (ev: Record<string, any> | null | undefined) =>
  DAY_STATUS[String(ev?.dayStatus || '')] || null;

export const exceptionCountOf = (ev: Record<string, any> | null | undefined): number =>
  Math.max(0, Number(ev?.exceptions) || 0);

/** First in / last out / gross / break as shown — "—" when not an
 *  attendance row (field, leave), never a fabricated zero. */
export const attendanceCells = (ev: Record<string, any> | null | undefined) => {
  const isScan = ev?.rule === 'attendance_scan';
  return {
    firstIn: isScan && ev?.clockIn ? String(ev.clockIn) : '—',
    lastOut: isScan && ev?.clockOut && !ev?.inProgress ? String(ev.clockOut) : '—',
    lastSeen: isScan && ev?.clockOut && ev?.inProgress ? String(ev.clockOut) : null,
    gross: isScan && ev?.grossMinutes !== undefined && !ev?.inProgress ? fmtHM(ev.grossMinutes) : '—',
    brk: isScan && ev?.breakMinutes !== undefined && !ev?.inProgress ? fmtHM(ev.breakMinutes) : '—',
  };
};

export interface FreshnessNote {
  tone: 'ok' | 'warning' | 'none';
  key: string;
  defaultValue: string;
  values?: Record<string, string | number>;
}

/** What the board says about scanner freshness. ⚠️ No data is not good
 *  news: a stale connector is said loudly, with what it means for Missing. */
export const freshnessNote = (
  f: { connected: boolean; lastContactAt?: string | null; stale: boolean; staleAfterMinutes: number; coveredThrough?: string } | undefined,
  fmtTime: (iso: string) => string,
): FreshnessNote => {
  if (!f || !f.connected) return { tone: 'none', key: '', defaultValue: '' };
  const last = f.lastContactAt ? fmtTime(f.lastContactAt) : '—';
  const covers = f.coveredThrough || '—';
  if (f.stale) {
    return {
      tone: 'warning', key: 'wa.fresh_stale',
      defaultValue: 'Scanner last contact {{last}} · data covers to {{covers}}. The scanner has not synced for over {{mins}} minutes — people on scanner branches are shown as "Awaiting scanner sync", not Missing, until it does.',
      values: { last, covers, mins: f.staleAfterMinutes },
    };
  }
  return { tone: 'ok', key: 'wa.fresh_ok', defaultValue: 'Scanner last contact {{last}} · data covers to {{covers}}', values: { last, covers } };
};
